<div class="flex flex-col h-full">
  <app-page-header [wrap]="true">
    <app-my-check-tabs [active]="'schedules'"></app-my-check-tabs>
    <div class="flex flex-row flex-wrap items-center justify-end gap-4">
      <div class="flex-1 min-w-[140px] max-w-[268px]">
        <bic-input (valueChange)="onSearch($event)"
                   [iconStart]="faMagnifyingGlass"
                   [placeholder]="'Suche nach...'"
                   [value]="searchQuery">
        </bic-input>
      </div>
      @if (canManage) {
        <bic-button (onClick)="openCreate()" [loading]="checkOptionsLoading" buttonType="primary">
          <fa-icon [icon]="faPlus" iconLeft></fa-icon>
          Neuer Zeitplan
        </bic-button>
      } @else {
        <span [tooltip]="noPermissionTooltip" tooltipDirection="bottom">
          <bic-button [disabled]="true" buttonType="primary">
            <fa-icon [icon]="faPlus" iconLeft></fa-icon>
            Neuer Zeitplan
          </bic-button>
        </span>
      }
    </div>
  </app-page-header>

  <div class="flex flex-col flex-1 min-h-0 p-6 overflow-hidden">
    @if (loading) {
      <div class="flex flex-col flex-1 justify-center items-center py-16">
        <div class="flex flex-row items-center gap-2">
          <fa-icon [icon]="faSpinnerThird" class="text-gray-500 animate-spin text-svg-16"></fa-icon>
          <span class="text-sm font-medium text-gray-500">Zeitpläne werden geladen...</span>
        </div>
      </div>
    } @else if (loadError) {
      <div class="flex flex-row items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
        <fa-icon [icon]="faTriangleExclamation" class="text-sm text-red shrink-0"></fa-icon>
        <span class="flex-1 text-sm text-red">Zeitpläne konnten nicht geladen werden.</span>
        <bic-button (onClick)="load()" buttonType="white">Erneut versuchen</bic-button>
      </div>
    } @else if (rows.length === 0) {
      <app-empty-state [icon]="faCalendarClock"
                       message="Noch keine Zeitpläne in diesem Projekt. Lege einen Zeitplan an, um einen Prüflauf automatisch auszuführen."
                       class="flex-1">
        @if (canManage) {
          <bic-button (onClick)="openCreate()" [loading]="checkOptionsLoading" buttonType="white">
            <fa-icon [icon]="faPlus" iconLeft></fa-icon>
            Neuer Zeitplan
          </bic-button>
        } @else {
          <span [tooltip]="noPermissionTooltip" tooltipDirection="top">
            <bic-button [disabled]="true" buttonType="white">
              <fa-icon [icon]="faPlus" iconLeft></fa-icon>
              Neuer Zeitplan
            </bic-button>
          </span>
        }
      </app-empty-state>
    } @else if (filteredRows.length === 0) {
      <app-empty-state [icon]="faMagnifyingGlass" message="Keine Zeitpläne gefunden." class="flex-1"></app-empty-state>
    } @else {
      <div class="flex-1 min-h-0 overflow-hidden border border-gray-200 rounded-lg flex flex-col">
        <div class="w-full flex-1 overflow-y-auto overflow-x-hidden">
          <table>
            <thead>
              <tr>
                <th class="w-[34%]">Prüflauf</th>
                <th class="w-[16%]">Nächste Ausführung</th>
                <th class="w-[22%]">Letzte Ausführung</th>
                <th class="w-[11%]">Status</th>
                <th class="w-[13%]">Ausgeführt als</th>
                <th class="!w-[52px] !p-0"></th>
              </tr>
            </thead>
            <tbody>
              @for (row of filteredRows; track row.schedule.id) {
                <tr>
                  <td [title]="row.name">
                    <div class="flex flex-col min-w-0">
                      @if (row.reportInstanceId) {
                        <button (click)="openReport(row)" class="truncate text-left font-medium text-gray-900 hover:underline"
                                tooltip="Bericht des letzten Laufs öffnen" tooltipDirection="top" type="button">
                          {{ row.name }}
                        </button>
                      } @else {
                        <span class="truncate font-medium">{{ row.name }}</span>
                      }
                      <span class="truncate text-xs text-gray-500" [title]="row.recurrence">{{ row.recurrence }}</span>
                    </div>
                  </td>
                  <td>{{ row.nextRun }}</td>
                  <td>
                    @if (row.lastRun) {
                      <!-- Datum und Ergebnis in einer Zeile. Das Badge kürzt erst bis 6rem (Tooltip zeigt den
                           vollen Text), bevor es bei schmaler Spalte in die nächste Zeile umbricht. -->
                      <div class="flex flex-row flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                        <span class="shrink-0">{{ row.lastRun }}</span>
                        @if (row.lastOutcome) {
                          <span [tooltip]="row.lastOutcome" class="flex grow basis-24 min-w-0 max-w-fit" tooltipDirection="top">
                            <bic-badge [bgClass]="row.lastOutcomeColors.bgClass" [textClass]="row.lastOutcomeColors.textClass"
                                       class="flex max-w-full">
                              <span class="truncate">{{ row.lastOutcome }}</span>
                            </bic-badge>
                          </span>
                        }
                      </div>
                    } @else {
                      –
                    }
                  </td>
                  <td>
                    <div class="flex flex-row items-center gap-2">
                      <span [tooltip]="row.statusTooltip" tooltipDirection="top">
                        <bic-badge [bgClass]="row.statusColors.bgClass" [textClass]="row.statusColors.textClass">
                          {{ row.status }}
                        </bic-badge>
                      </span>
                      @if (row.eligibilityIssue) {
                        <fa-icon [icon]="faTriangleExclamation" [tooltip]="row.eligibilityIssue"
                                 class="text-sm text-yellow shrink-0" tooltipDirection="top"></fa-icon>
                      }
                    </div>
                  </td>
                  <td [title]="row.schedule.actingUserName ?? ''">{{ row.schedule.actingUserName ?? '–' }}</td>
                  <td class="!w-[52px] !p-0">
                    <button (click)="$event.stopPropagation()"
                            [bicDropdownTrigger]="rowMenu"
                            class="flex items-center justify-center w-10 h-[38px] rounded-lg hover:bg-gray-100 transition-colors shrink-0 mx-auto"
                            title="Aktionen" type="button">
                      @if (busyIds.has(row.schedule.id)) {
                        <fa-icon [icon]="faSpinnerThird" class="text-svg-16 text-gray-500 animate-spin"></fa-icon>
                      } @else {
                        <fa-icon [icon]="faEllipsisVertical" class="text-svg-16 text-gray-900"></fa-icon>
                      }
                    </button>
                    <bic-dropdown #rowMenu [dropdownPlacement]="'bottom-end'">
                      @if (canManage) {
                        <bic-dropdown-element (click)="rowMenu.hide(); openEdit(row.schedule)">
                          <div class="flex flex-row items-center gap-2 text-sm text-gray-900">
                            <fa-icon [icon]="faPen" class="text-sm text-gray-700 w-4"></fa-icon>
                            <span>Bearbeiten</span>
                          </div>
                        </bic-dropdown-element>
                        @if (row.schedule.status === 'ACTIVE') {
                          <bic-dropdown-element (click)="rowMenu.hide(); pause(row.schedule)">
                            <div class="flex flex-row items-center gap-2 text-sm text-gray-900">
                              <fa-icon [icon]="faPause" class="text-sm text-gray-700 w-4"></fa-icon>
                              <span>Pausieren</span>
                            </div>
                          </bic-dropdown-element>
                        } @else if (row.schedule.status === 'PAUSED') {
                          <bic-dropdown-element (click)="rowMenu.hide(); resume(row.schedule)">
                            <div class="flex flex-row items-center gap-2 text-sm text-gray-900">
                              <fa-icon [icon]="faPlay" class="text-sm text-gray-700 w-4"></fa-icon>
                              <span>Fortsetzen</span>
                            </div>
                          </bic-dropdown-element>
                        }
                        <bic-dropdown-element (click)="rowMenu.hide(); requestDelete(row.schedule)">
                          <div class="flex flex-row items-center gap-2 text-sm text-red">
                            <fa-icon [icon]="faTrash" class="text-sm w-4"></fa-icon>
                            <span>Löschen</span>
                          </div>
                        </bic-dropdown-element>
                      } @else {
                        <bic-dropdown-element (click)="$event.stopPropagation()">
                          <div [tooltip]="noManageTooltip" class="flex flex-row items-center gap-2 text-sm text-gray-400 cursor-not-allowed"
                               tooltipDirection="top">
                            <fa-icon [icon]="faPen" class="text-sm text-gray-400 w-4"></fa-icon>
                            <span>Bearbeiten</span>
                          </div>
                        </bic-dropdown-element>
                        <bic-dropdown-element (click)="$event.stopPropagation()">
                          <div [tooltip]="noManageTooltip" class="flex flex-row items-center gap-2 text-sm text-gray-400 cursor-not-allowed"
                               tooltipDirection="top">
                            <fa-icon [icon]="row.schedule.status === 'PAUSED' ? faPlay : faPause" class="text-sm text-gray-400 w-4"></fa-icon>
                            <span>{{ row.schedule.status === 'PAUSED' ? 'Fortsetzen' : 'Pausieren' }}</span>
                          </div>
                        </bic-dropdown-element>
                        <bic-dropdown-element (click)="$event.stopPropagation()">
                          <div [tooltip]="noManageTooltip" class="flex flex-row items-center gap-2 text-sm text-gray-400 cursor-not-allowed"
                               tooltipDirection="top">
                            <fa-icon [icon]="faTrash" class="text-sm text-gray-400 w-4"></fa-icon>
                            <span>Löschen</span>
                          </div>
                        </bic-dropdown-element>
                      }
                    </bic-dropdown>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  </div>
</div>

@if (popupSchedule !== undefined && projectId != null) {
  <app-check-run-schedule-popup
    (closed)="closePopup()"
    (configureDefinition)="openBuilder($event)"
    (deleted)="onDeletedInPopup()"
    (saved)="onSaved()"
    [checkOptions]="checkOptions"
    [projectId]="projectId"
    [schedule]="popupSchedule">
  </app-check-run-schedule-popup>
}

@if (deleteTarget) {
  <bic-popup (close)="cancelDelete()" [hasBackdrop]="true" customClass="w-[560px] h-fit" title="Zeitplan löschen?">
    <div bicPopupContent class="flex flex-col">
      <span class="font-normal">Möchten Sie den Zeitplan für „{{ deleteTarget.definitionName ?? 'Prüflauf' }}" löschen?<br><br>
        Der Prüflauf selbst und bereits erstellte Berichte bleiben erhalten.</span>
    </div>
    <div bicPopupButtonGroup class="flex flex-row w-full justify-end items-center gap-4">
      <bic-button (onClick)="cancelDelete()" [disabled]="deleting" buttonType="white">Abbrechen</bic-button>
      <bic-button (onClick)="confirmDelete()" [loading]="deleting" buttonType="primary">Löschen</bic-button>
    </div>
  </bic-popup>
}
